import { useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Clock,
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
} from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../../lib/utils";

const CAL_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function WorkoutCalendar({
  schedule = [],
  currentMonth,
  onMonthChange,
  selectedDateStr,
  onSelectDate,
  isRePrepLocked = false,
  todayStr,
  isSelfWorkoutMode,
}) {
  const sessionMap = useMemo(() => {
    const map = {};
    schedule.forEach((s) => {
      if (s.date) map[s.date] = s;
    });
    return map;
  }, [schedule]);

  const hasStatus = useMemo(() => {
    const res = {
      completed: false,
      in_progress: false,
      missed: false,
      skipped: false,
      active: false,
      prep: false,
      reprep: false,
      rest: false,
    };
    if (!schedule) return res;
    schedule.forEach((s) => {
      if (s.status === "completed") res.completed = true;
      if (s.status === "in_progress") res.in_progress = true;
      if (s.status === "missed" || s.isUnresolved) res.missed = true;
      if (s.status === "skipped") res.skipped = true;
      if (s.phase === "active") res.active = true;
      if (s.phase === "fitness_preparation") res.prep = true;
      if (s.phase === "fitness_re_preparation") res.reprep = true;
      if (s.phase === "rest") res.rest = true;
    });
    return res;
  }, [schedule]);

  const anyStatusPresent = useMemo(
    () => Object.values(hasStatus).some(Boolean),
    [hasStatus],
  );

  const getScheduleForDate = (dateObj) => {
    const ymd = format(dateObj, "yyyy-MM-dd");
    return sessionMap[ymd];
  };

  const today = new Date();
  const todayYMD = todayStr || format(today, "yyyy-MM-dd");

  const todayOfWeekIdx = (() => {
    if (todayStr) {
      const parts = todayStr.split("-").map(Number);
      if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2]).getDay();
      }
    }
    return today.getDay();
  })();

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between px-3 py-2 border-b border-border/40 gap-2 space-y-0">
        <CardTitle className="text-xs font-semibold uppercase tracking-tight  font-black">
          Calendar
        </CardTitle>
        <div className="flex items-center">
          <button
            type="primary"
            onClick={() => {
              const targetDate = new Date();
              const targetYMD = todayStr || format(targetDate, "yyyy-MM-dd");
              onSelectDate(targetYMD);
            }}
            className="text-xs font-semibold  tracking-tight px-2 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-foreground cursor-pointer"
          >
            Today
          </button>
          <button
            onClick={() => onMonthChange(subMonths(currentMonth, 1))}
            className="w-7 h-7 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors text-foreground border-0 cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs font-semibold tracking-tight uppercase text-center min-w-[90px]">
            {format(currentMonth, "MMM yyyy")}
          </span>
          <button
            onClick={() => onMonthChange(addMonths(currentMonth, 1))}
            className="w-7 h-7 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors text-foreground border-0 cursor-pointer"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-1">
        <div className="grid grid-cols-7 gap-1 mb-1 border-b border-border/20 pb-1">
          {CAL_HEADERS.map((h, hIdx) => (
            <div
              key={h}
              className={cn(
                "text-center text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-semibold py-1 px-0.5 transition-all",
                hIdx === todayOfWeekIdx
                  ? "text-xs font-mono flex items-center justify-center rounded-md transition-all  bg-foreground text-background font-black shadow-sm"
                  : "",
              )}
            >
              {h}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 mt-5">
          {eachDayOfInterval({
            start: startOfMonth(currentMonth),
            end: endOfMonth(currentMonth),
          }).map((day, idx) => {
            const dayOfWeek = day.getDay();
            const isFirstDay = idx === 0;
            const scheduleEntry = getScheduleForDate(day);
            const ymd = format(day, "yyyy-MM-dd");
            const isHiddenDueToLock =
              isRePrepLocked &&
              ymd >= todayStr &&
              scheduleEntry?.status !== "completed" &&
              scheduleEntry?.status !== "missed";
            const scheduleEntryToUse = isHiddenDueToLock ? null : scheduleEntry;
            const status = scheduleEntryToUse?.status;
            const isSelected = selectedDateStr === ymd;
            const isToday = ymd === todayYMD;

            return (
              <div
                key={day.toISOString()}
                style={{
                  gridColumn: isFirstDay ? dayOfWeek + 1 : undefined,
                }}
                onClick={() =>
                  (isSelfWorkoutMode || scheduleEntryToUse) && onSelectDate(ymd)
                }
                className={cn(
                  "min-h-[52px] p-1 rounded-md border transition-all flex flex-col justify-between select-none relative",
                  isSelfWorkoutMode || scheduleEntryToUse
                    ? "cursor-pointer"
                    : "cursor-default opacity-40",
                  status === "completed"
                    ? "border-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/25 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 shadow-sm"
                    : status === "in_progress"
                      ? "border-amber-500 bg-amber-500/10 dark:bg-amber-500/25 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 shadow-sm"
                      : status === "missed"
                        ? "border-red-500 bg-red-500/10 dark:bg-red-500/25 hover:bg-red-500/20 text-red-700 dark:text-red-400 shadow-sm"
                        : status === "skipped"
                          ? "border-dashed border-muted-foreground/35 bg-muted/5 dark:bg-muted/10 text-muted-foreground hover:bg-muted/15"
                          : scheduleEntryToUse?.phase === "fitness_preparation"
                            ? "border-purple-500/20 bg-purple-500/5 hover:border-purple-500/45 text-purple-700 dark:text-purple-400"
                            : scheduleEntryToUse?.phase ===
                                "fitness_re_preparation"
                              ? "border-pink-500/20 bg-pink-500/5 hover:border-pink-500/45 text-pink-700 dark:text-pink-400"
                              : scheduleEntryToUse?.phase === "rest"
                                ? "border-border bg-secondary/15 hover:bg-secondary/35 text-muted-foreground"
                                : scheduleEntryToUse?.phase === "active"
                                  ? "border-blue-500/20 bg-blue-500/5 hover:border-blue-500/45 text-blue-700 dark:text-blue-400"
                                  : status
                                    ? "border-amber-500 bg-amber-500/10 hover:border-amber-500/40 text-amber-700 dark:text-amber-400"
                                    : "border-border/60 bg-secondary/20 hover:bg-secondary/40",
                  isToday &&
                    "ring-2 ring-foreground/20 border-foreground/30 shadow-md",
                  isSelected &&
                    "ring-2 ring-zinc-800 dark:ring-zinc-200 ring-offset-2 dark:ring-offset-background border-zinc-800 dark:border-zinc-200",
                )}
              >
                {isToday && (
                  <div className="absolute -top-7 left-0 -translate-x-1/2 z-20 pointer-events-none flex flex-col items-center animate-bounce">
                    <span className="bg-zinc-950 dark:bg-zinc-50 text-zinc-50 dark:text-zinc-950 text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap">
                      Today
                    </span>
                    <span className="text-zinc-950 dark:text-zinc-50 text-[8px] leading-none -mt-0.5 select-none">
                      ▼
                    </span>
                  </div>
                )}
                <div className="flex flex-col justify-between gap-0 w-full">
                  <div className="flex items-center justify-between w-full">
                    <div
                      className={cn(
                        "text-xs font-mono font-bold flex items-center justify-center rounded-full transition-all w-5 h-5",
                        isToday
                          ? "bg-foreground text-background font-black shadow-sm"
                          : "text-muted-foreground",
                        !isToday &&
                          (status === "completed"
                            ? "text-emerald-600 dark:text-emerald-400 font-bold"
                            : status === "in_progress"
                              ? "text-amber-600 dark:text-amber-400 font-bold animate-pulse"
                              : status === "missed"
                                ? "text-red-600 dark:text-red-400 font-bold"
                                : status === "skipped"
                                  ? "text-muted-foreground/50 font-normal line-through"
                                  : "text-muted-foreground"),
                      )}
                    >
                      {format(day, "d")}
                    </div>
                  </div>
                  {scheduleEntryToUse?.phase && (
                    <div className="flex flex-col gap-0.5 mt-0.5 w-full select-none">
                      <div
                        className={cn(
                          "text-[7.5px] uppercase font-black leading-none flex w-full text-left",
                          scheduleEntryToUse.phase === "active"
                            ? "text-blue-500"
                            : scheduleEntryToUse.phase === "rest"
                              ? "text-muted-foreground"
                              : scheduleEntryToUse.phase ===
                                  "fitness_preparation"
                                ? "text-purple-500"
                                : "text-pink-500",
                        )}
                      >
                        {(() => {
                          const num = scheduleEntryToUse.workoutDayNumber || 1;
                          if (scheduleEntryToUse.phase === "active")
                            return `D${num}`;
                          if (
                            scheduleEntryToUse.phase === "fitness_preparation"
                          )
                            return `Prep D${num}`;
                          if (
                            scheduleEntryToUse.phase ===
                            "fitness_re_preparation"
                          )
                            return `Re-Prep D${num}`;
                          if (scheduleEntryToUse.phase === "rest")
                            return `Rest D${num}`;
                          if (scheduleEntryToUse.phase === "self_workout")
                            return `Self`;
                          return `D${num}`;
                        })()}
                      </div>
                      {scheduleEntryToUse.workoutSession?.label && (
                        <div className="text-[7.5px] font-semibold text-foreground/80 leading-none truncate max-w-full capitalize mt-0.5">
                          {scheduleEntryToUse.workoutSession.label}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {scheduleEntryToUse && (
                  <div className="space-y-0.5 mt-auto flex items-center justify-between w-full">
                    {status === "completed" ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 shrink-0 ml-auto" />
                    ) : status === "in_progress" ? (
                      <Clock className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 shrink-0 ml-auto animate-bounce" />
                    ) : status === "missed" ? (
                      <XCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0 ml-auto animate-bounce" />
                    ) : status === "skipped" ? (
                      <MinusCircle className="h-3.5 w-3.5 text-muted-foreground/45 shrink-0 ml-auto" />
                    ) : status === "upcoming" && isSelfWorkoutMode ? (
                      <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 ml-auto animate-bounce " />
                    ) : (
                      <div
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          "bg-amber-500",
                        )}
                      ></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {schedule && schedule.length > 0 && anyStatusPresent && (
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-3 pt-3 border-t border-border/40 select-none">
            {hasStatus.completed && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Completed
                </span>
              </div>
            )}
            {hasStatus.in_progress && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  In Progress
                </span>
              </div>
            )}
            {hasStatus.missed && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Missed
                </span>
              </div>
            )}
            {hasStatus.skipped && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 border border-dashed border-muted-foreground/45 bg-muted/15 rounded-full animate-pulse-subtle" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Skipped
                </span>
              </div>
            )}
            {hasStatus.active && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Active
                </span>
              </div>
            )}
            {hasStatus.prep && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Prep
                </span>
              </div>
            )}
            {hasStatus.reprep && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-pink-500" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Re-Prep
                </span>
              </div>
            )}
            {hasStatus.rest && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 border border-border bg-secondary/15 rounded-full" />
                <span className="text-[9px] font-mono text-muted-foreground">
                  Rest
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
